import { isConditionFulfilled } from "./conditionFunctions";

import type {
  ConditionActivatorType,
  ConditionComparedType,
  ConditionItem,
  ConditionItemProperty,
  ConditionOperator,
  ConditionTypeId
} from "./ConditionItem";
import type { SimulationRoundItem } from "@editor/items/simulationRound/SimulationRoundItem";

class Condition<
  A extends ConditionActivatorType = ConditionActivatorType,
  C extends ConditionComparedType = ConditionComparedType
> implements ConditionItem<A, C>
{
  id: number;
  uid: `condition_${number}`;
  type: "condition";
  createdAt: number;
  doc: string;
  order: number;

  srcType: ConditionItem["srcType"];
  srcId: number | null;

  activatorType: A;
  activatorId: ConditionTypeId<A>;
  activatorProperty: ConditionItemProperty<A>;
  activatorPropertyVariant: "positive" | "negative" | null;

  operator: ConditionOperator | null;

  activatorStart: number | null;
  activatorStartType: ConditionItem["activatorStartType"];
  activatorEnd: number | null;
  activatorEndType: ConditionItem["activatorEndType"];

  comparedType: C | null;
  comparedValue: number | null;
  comparedValueType: "number" | "absoluteRound" | "relativeRound";
  comparedId: ConditionTypeId<C>;
  comparedProperty: ConditionItemProperty<C>;

  comparedStartType:
    | "absoluteRound"
    | "relativeRound"
    | "startRound"
    | "currentRound"
    | null;
  comparedStart: number | null;
  comparedEndType: "absoluteRound" | "relativeRound" | "currentRound" | null;
  comparedEnd: number | null;

  constructor(conditionData: ConditionItem<A, C>) {
    this.type = "condition";
    this.id = conditionData.id;
    this.uid = conditionData.uid;
    this.createdAt = conditionData.createdAt;

    this.srcType = conditionData.srcType;
    this.srcId = conditionData.srcId;
    this.doc = conditionData.doc;
    this.order = conditionData.order;

    this.activatorType = conditionData.activatorType;
    this.activatorId = conditionData.activatorId;
    this.activatorProperty = conditionData.activatorProperty;
    this.activatorPropertyVariant = conditionData.activatorPropertyVariant;

    this.operator = conditionData.operator;

    this.activatorStart = conditionData.activatorStart;
    this.activatorStartType = conditionData.activatorStartType;
    this.activatorEnd = conditionData.activatorEnd;
    this.activatorEndType = conditionData.activatorEndType;

    this.comparedType = conditionData.comparedType;
    this.comparedValue = conditionData.comparedValue;
    this.comparedValueType = conditionData.comparedValueType;
    this.comparedId = conditionData.comparedId;
    this.comparedProperty = conditionData.comparedProperty;

    this.comparedStartType = conditionData.comparedStartType;
    this.comparedStart = conditionData.comparedStart;
    this.comparedEndType = conditionData.comparedEndType;
    this.comparedEnd = conditionData.comparedEnd;
  }

  /**
   * Check if the condition applies in this round
   * and (if it is of singular type) the last round
   * Use this for a correct condition check.
   * @param round - the round to check
   */
  evaluate(rounds: SimulationRoundItem[]) {
    if (rounds.length === 0) return false;

    return isConditionFulfilled(this, rounds);
  }

  /**
   * Check if all data to evaluate the condition is provided.
   * A type and an operator are needed.
   */
  get complete() {
    if (
      this.activatorType === "action" ||
      this.activatorType === "incident" ||
      this.activatorType === "target" ||
      this.activatorType === "resource" ||
      this.activatorType === "index"
    ) {
      if (this.activatorId == null) return false;
      if (this.operator == null) return false;
    }
    return true;
  }
}

export default Condition;
